/**
 * exam-center-route — 「出考卷中心」的**純路由核心**(無 DOM,可單測)。
 *
 * boss 2026-06-14「應該讓使用者選課程去產生」:選科目 + 章節/單元/課 → 產生對應
 * 那個範圍的考卷。本檔只負責「決定一個範圍(科目 + KP)要用哪種方式出卷」,UI 與
 * 實際渲染在 `exam-center.ts`。
 *
 * 三種出卷方式(對應 boss「標示產生方式」要求):
 *   - `'program'`(✅ 程式產生,答案可驗證):走 `generateMathExam` / `generateListeningExam`
 *     範本產生器 —— 每題答案程式反算可驗。
 *   - `'bank'`(📚 題庫組卷):從人工轉錄的題庫檔抽題(`getBankUrl` → `loadBankFor`)。
 *     題庫是人工轉錄較穩(memory feedback_chinese_zhuyin_accuracy:國語注音題優先走題庫)。
 *
 * **覆蓋鐵則**:每一課 / 單元都生得出一張卷 —— 路由保證對任一 curriculum KP 都回一個
 * 可執行 plan(優先 program,其次 bank;兩者皆無時 graceful fallback 到該科預設產生器
 * 或最接近的題庫),不會「按了沒反應」。
 *
 * 課程索引來源:`src/learn/curriculum/index.ts` 的 `CURRICULUM`(版本:康軒國/數、
 * 南一自、翰林社/英,見 project_textbook_versions)。題庫對照:`entry/bank-lookup.ts`。
 *
 * 學習/遊戲分離:純學習側資料路由,絕不碰 commanderXp / 戰鬥數值。
 */

import { CURRICULUM } from '../curriculum/index';
import { getBankUrl } from '../entry/bank-lookup';
import type { Subject, GradeSemester } from '../../shared/types';
import type { MathSectionKind } from './math-exam-gen';

/** 出卷方式:程式產生(可驗證)/ 題庫組卷(人工轉錄)。 */
export type ExamGenMethod = 'program' | 'bank';

/**
 * 學年度小數標記(對齊 `KPEntry.grade`):3.1 = 三上、3.2 = 三下、4.1 = 四上、4.2 = 四下。
 * 「出考卷中心」預設只列**當前學年**的單元(boss 2026-06-14「只顯示三下 9 個單元、
 * 其餘學年收進複習摺疊」),避免一個畫面攤平幾十顆按鈕。
 * 單一真相在 `shared/types.ts`;此處 re-export 供既有 import 點不變。
 */
export type { GradeSemester };

/** 當前學年預設值 = 三下(profile 沒設 `currentGradeSemester` 時用此)。 */
export const DEFAULT_GRADE_SEMESTER: GradeSemester = 3.2;

/** 一個可選範圍(科目下的一課 / 單元 / 知識點),供 UI 列出。 */
export interface ExamRange {
  /** KP id(`math.div.remainder` / `chinese.l03.*` …)。 */
  kpId: string;
  /** 顯示名(課本上的單元 / 課名)。 */
  label: string;
  /** 所屬章節標題(分組用)。 */
  chapter: string;
  /** 章節序號(排序用)。 */
  chapterNumber: number;
}

/**
 * 一個**單元級**範圍(= 一個課本章節 = UI 一顆大按鈕)。boss「一個單元一顆按鈕,
 * 點下去直接出那單元一整張卷,不再攤 KP / 不再多一層選小知識點」。
 *
 * 一個單元可能含多個 KP;`planExamForUnit` 會把它們**合成一張該單元的卷**
 * (數學合併大題、其他科合併題庫範圍),家長不必再選。
 */
export interface ExamUnit {
  /** 章節序號(課本單元序,排序用)。 */
  chapterNumber: number;
  /** 單元顯示名(= 課本章節標題,如「除法」「統計表」)。 */
  title: string;
  /** 此單元所屬學年(3.1 / 3.2 / 4.1),由章節 KP 的 grade 推得。 */
  gradeSemester: GradeSemester;
  /** 此單元的 KP id 清單(依課程序;供路由 / 合成卷用)。 */
  kpIds: string[];
  /** 代表性 KP id(路由 + 錯題回流落點用,取第一個能驅動產生器的 KP)。 */
  kpId: string;
}

/**
 * 一個範圍的出卷計畫。`method` 決定 UI 標示與渲染路徑:
 *   - program + math:用 `mathKps`(MathSectionKind 子集)呼叫 `generateMathExam`。
 *   - program + english-listening:呼叫 `generateListeningExam`(無額外參數)。
 *   - bank:用 `bankKpId`(必為有題庫的 KP)呼叫 `loadBankFor` + `sampleGroupedPractice`。
 */
export interface ExamPlan {
  subject: Subject;
  kpId: string;
  /** 範圍顯示名(= ExamRange.label)。 */
  label: string;
  method: ExamGenMethod;
  /** UI 標籤:✅ 程式產生(答案可驗證)/ 📚 題庫組卷。 */
  methodLabel: string;
  /**
   * program 路徑:'math' = 數學產生器、'listening' = 英語聽力產生器、
   * 'stats-table' = 統計表(表格判讀)產生器、'time' = 時間(換算 / AM-PM / 加減)產生器、
   * 'mul-div' = 乘法與除法(乘除互逆 / 未知數 / 倍數 / 直式除法含驗算 / 應用題)產生器、
   * 'circle' = 圓(半徑↔直徑換算 / 名詞認識 / 判斷對錯 / 用圓規畫圓 / 圓規操作)產生器、
   * 'decimal' = 小數(分數↔一位小數 / 看圖數線 / 比大小 / 加減 / 毫米換算與應用)產生器。
   */
  programKind?: 'math' | 'listening' | 'stats-table' | 'time' | 'mul-div' | 'circle' | 'decimal';
  /** program + math:要出的大題(MathSectionKind 子集),空 = 四大題全出。 */
  mathKps?: MathSectionKind[];
  /** bank 路徑:實際撈題用的 KP id(可能 = kpId,fallback 時可能換成同章有題庫的 KP)。 */
  bankKpId?: string;
}

export const METHOD_LABEL: Record<ExamGenMethod, string> = {
  program: '✅ 程式產生(答案可驗證)',
  bank: '📚 題庫組卷',
};

/** 數學專屬程式產生器種類(非 generateMathExam 七大題)。 */
export type MathSpecialProgram = 'stats-table' | 'time' | 'mul-div' | 'circle' | 'decimal';

/**
 * 數學「走專屬程式產生器」(非 generateMathExam 七大題)的 KP → programKind 映射。
 *   - 統計表(表格判讀)單元 `math.stats.table` → `generateStatsTableExam`(七表型,答案從表反算可驗證)。
 *   - 時間單元 `math.time.clock` / `math.time.duration` → `generateTimeExam`(一日24小時 / 12↔24 換算 /
 *     AM-PM / 時分秒加減,答案由換算 + 分鐘數反算可驗證,round-trip 一致)。
 *   - 第 7 單元「乘法與除法」`math.div.mul-relation` → `generateMulDivExam`(乘除互逆四算式 /
 *     未知數三型 / 倍數倍器數線 / 直式除法含餘數驗算 / 乘除應用題,答案 recompute 反算可驗證)。
 *     **此單元走 mul-div 專屬產生器,不走 generateMathExam 的 mulvert(乘法直式,三上)路徑** ——
 *     `math.div.mul-relation` 命中專屬程式優先於數學大題聯集,故第 7 單元卷不含 mulvert。
 *   - 第 6 單元「圓」`math.shape.circle` → `generateCircleExam`(半徑↔直徑換算 recompute /
 *     名詞認識 curated / 判斷對錯 curated / 用圓規畫圓 recompute / 圓規操作列印題)。
 *     **此單元改走 circle 專屬產生器,從 generic/bank 移除**(原本走題庫,boss 要程式可驗證 +
 *     概念/判斷 curated)。命中專屬程式優先於該單元題庫,故第 6 單元卷走程式而非題庫。
 *   - 第 5 單元「小數」`math.decimal.basic` / `math.decimal.addsub` → `generateDecimalExam`
 *     (分數↔一位小數互換 recompute / 看圖數線寫小數 / 一位小數比大小 / 加減含進退位 /
 *     毫米↔公分換算與情境應用,**全程十分位整數運算杜絕浮點誤差**,recompute 反算可驗證)。
 *     **此單元改走 decimal 專屬產生器,從 generic/bank 移除**(原本 generateMathExam 七大題 fallback
 *     不含小數題;boss 要程式可驗證 + 嚴格一位小數不超綱)。命中專屬程式優先於該單元題庫 /
 *     數學大題映射,故第 5 單元卷走 decimal 程式而非題庫 / 通用數學卷。
 * **比題庫優先 + 比 generateMathExam 大題映射優先**(boss 要程式可驗證的卷,且去除三上 mulvert)。
 */
const MATH_SPECIAL_PROGRAM: Record<string, MathSpecialProgram> = {
  'math.stats.table': 'stats-table',
  'math.time.clock': 'time',
  'math.time.duration': 'time',
  'math.div.mul-relation': 'mul-div',
  'math.shape.circle': 'circle',
  'math.decimal.basic': 'decimal',
  'math.decimal.addsub': 'decimal',
};

// ───────────────────────────── 數學單元 → 產生器大題映射 ─────────────────────────────

/**
 * 數學 KP → `generateMathExam` 的大題(MathSectionKind)子集映射(boss「映射表寫清楚」)。
 *
 * 產生器七大題:compute(直式除法,整除/有餘混合)/ mulvert(乘法直式)/
 * arrow(算式填空 round-trip)/ inverse(乘除互換)/ remword(有餘數應用,進位/捨去)/
 * twostep(兩步驟文字題)/ word(平分・倍數單步應用題)。映射原則 = 該單元概念最貼近:
 *   - 有餘數除法單元 → compute + inverse + remword + word(餘數情境是核心鑑別)。
 *   - 整除單元 → compute + inverse + word。
 *   - 乘除關係單元 → inverse + arrow + word。
 *   - 乘法直式單元 → mulvert + arrow + inverse + word。
 *   - 兩步驟 / 數與四則單元 → twostep + word(+ compute 練直式)。
 * 未列出的數學 KP → 預設七大題全出(空陣列代表 generateMathExam 預設行為,總分 100)。
 */
const MATH_KP_TO_SECTIONS: Record<string, MathSectionKind[]> = {
  // 第 3 章 除法:意義與直式計算
  'math.div.remainder': ['compute', 'inverse', 'remword', 'word'],
  'math.div.no-remainder': ['compute', 'inverse', 'word'],
  'math.div.mul-relation': ['inverse', 'arrow', 'remword', 'word'],
  'math.div.1digit.no-rem': ['compute', 'inverse', 'word'],
  // 第 2 章 乘法直式
  'math.mul.2x1': ['mulvert', 'arrow', 'inverse', 'word'],
  'math.mul.2x2': ['mulvert', 'arrow', 'inverse', 'word'],
  'math.mul.3x1': ['mulvert', 'arrow', 'inverse', 'word'],
  'math.mul.2x1.review': ['mulvert', 'arrow', 'inverse', 'word'],
  // 第 1 章 數與四則 / 兩步驟應用
  'math.word.two-step': ['twostep', 'word', 'compute'],
  'math.calc.addsub-vertical': ['compute', 'twostep', 'word'],
  // 四上 backport 乘除 / 兩步驟
  'math.word.two-step.4up': ['twostep', 'word', 'inverse'],
  'math.mul.2x2.4up': ['mulvert', 'arrow', 'inverse', 'word'],
  'math.mul.3x1.4up': ['mulvert', 'arrow', 'inverse', 'word'],
};

/**
 * 英語「適合用聽力產生器出卷」的 KP(對齊 listening-exam 池:jobs / 家庭 / Wh- 問句 /
 * minimal pair phonics)。這些單元按了 → `generateListeningExam` 出可驗證聽力卷。
 */
const ENGLISH_LISTENING_KPS: ReadonlySet<string> = new Set([
  'english.alphabet.phonics',
  'english.phonics.basic',
  'english.grammar.questions',
  'english.grammar.statements',
  'english.vocab.family',
  'english.dialogue.daily',
  'english.grammar.questions.meow911',
]);

// ───────────────────────────── 範圍列舉 ─────────────────────────────

/**
 * 列出一科所有可出卷的範圍(章節 / 單元 / 課),依章節序排序。
 * 直接從 `CURRICULUM` 讀,不硬編(boss「復用 academy 的章節資料,別硬編」)。
 */
export function listExamRanges(subject: Subject): ExamRange[] {
  const subj = CURRICULUM.find((s) => s.subject === subject);
  if (!subj) return [];
  const out: ExamRange[] = [];
  for (const ch of subj.chapters) {
    for (const kp of ch.kps) {
      out.push({
        kpId: kp.id,
        label: kp.label,
        chapter: ch.title,
        chapterNumber: ch.number,
      });
    }
  }
  out.sort((a, b) => a.chapterNumber - b.chapterNumber);
  return out;
}

/** 課程版本標籤(UI 顯示「康軒 / 南一 / 翰林」)。 */
export function textbookFor(subject: Subject): string {
  return CURRICULUM.find((s) => s.subject === subject)?.textbook ?? '';
}

// ───────────────────────────── 單元級範圍列舉(boss:一個單元一顆按鈕)─────────────────────────────

/**
 * 判斷一個章節是不是「真單元」(該列在出卷清單),排除段考真題庫等綜合題池:
 *   - chapter number 50(各科 `【三下】📚 段考真題庫` 綜合池)。
 *   - 標題含 📚(題庫池而非課本單元)。
 *   - 整章 KP 都是 `*.meow911*`(段考真題綜合,非單一單元)。
 * 這些是「複習 / 綜合」資產,不是課本單元,不該擋在「出一張單元卷」的清單裡。
 */
export function isRealUnitChapter(ch: { number: number; title: string; kps: { id: string }[] }): boolean {
  if (ch.number === 50) return false;
  if (ch.title.includes('📚')) return false;
  if (ch.kps.length > 0 && ch.kps.every((k) => k.id.includes('meow911'))) return false;
  return true;
}

/**
 * 推得一個章節所屬學年:取章節內**多數 KP 的 grade**(未填預設 3.2)。
 * 真課本三下單元的 KP 都標 `grade:3.2`,三上 backport 標 3.1、四上標 4.1,
 * 故同章 KP 學年一致;取第一個 KP 的 grade 即可,fallback 3.2。
 */
export function chapterGrade(ch: { kps: { grade?: GradeSemester }[] }): GradeSemester {
  for (const k of ch.kps) {
    if (k.grade != null) return k.grade;
  }
  return DEFAULT_GRADE_SEMESTER;
}

/**
 * 一個 KP 是否「能驅動產生器或有題庫」(可當單元代表 KP / 確保按了出得了卷)。
 * 數學一律可(產生器有 fallback);英語聽力池或有題庫可;其餘需有題庫。
 */
function kpIsLaunchable(subject: Subject, kpId: string): boolean {
  if (subject === 'math') return true; // 數學產生器永遠 fallback 得出卷
  if (subject === 'english') return true; // 聽力產生器池為三下通用 + 題庫 fallback
  return getBankUrl(kpId) !== null;
}

/**
 * 列出一科「當前學年」的**單元**(= 課本章節,一章一顆按鈕)。
 *
 * boss 2026-06-14:「練→自動出一張卷→數學」應**只列三下 9 個單元**,三上 / 四上
 * 章節從這裡拿掉(走複習摺疊),也不把單元下的 KP 攤成多顆按鈕。
 *
 * @param subject       科目。
 * @param gradeSemester 要列的學年(預設三下 3.2)。
 * @returns 該學年的真單元清單(依章節序),已排除段考真題庫等綜合池。
 */
export function listExamUnits(
  subject: Subject,
  gradeSemester: GradeSemester = DEFAULT_GRADE_SEMESTER,
): ExamUnit[] {
  const subj = CURRICULUM.find((s) => s.subject === subject);
  if (!subj) return [];
  const out: ExamUnit[] = [];
  for (const ch of subj.chapters) {
    if (!isRealUnitChapter(ch)) continue;
    if (chapterGrade(ch) !== gradeSemester) continue;
    const kpIds = ch.kps.map((k) => k.id);
    if (kpIds.length === 0) continue;
    // 代表 KP:取第一個「可驅動產生器 / 有題庫」的,確保按下去出得了卷。
    const repKp = kpIds.find((id) => kpIsLaunchable(subject, id)) ?? kpIds[0];
    out.push({
      chapterNumber: ch.number,
      title: ch.title,
      gradeSemester,
      kpIds,
      kpId: repKp,
    });
  }
  out.sort((a, b) => a.chapterNumber - b.chapterNumber);
  return out;
}

/**
 * 列出一科「其他學年(複習用)」的單元 —— 給 UI 的「▾ 複習舊單元」摺疊用。
 * = 全部真單元減去當前學年的單元,依學年(3.1 → 4.1)再依章節序排。
 */
export function listReviewUnits(
  subject: Subject,
  currentGradeSemester: GradeSemester = DEFAULT_GRADE_SEMESTER,
): ExamUnit[] {
  const subj = CURRICULUM.find((s) => s.subject === subject);
  if (!subj) return [];
  const out: ExamUnit[] = [];
  for (const ch of subj.chapters) {
    if (!isRealUnitChapter(ch)) continue;
    const g = chapterGrade(ch);
    if (g === currentGradeSemester) continue;
    const kpIds = ch.kps.map((k) => k.id);
    if (kpIds.length === 0) continue;
    const repKp = kpIds.find((id) => kpIsLaunchable(subject, id)) ?? kpIds[0];
    out.push({ chapterNumber: ch.number, title: ch.title, gradeSemester: g, kpIds, kpId: repKp });
  }
  out.sort((a, b) => (a.gradeSemester - b.gradeSemester) || (a.chapterNumber - b.chapterNumber));
  return out;
}

// ───────────────────────────── 路由核心 ─────────────────────────────

/**
 * 在同章節中找一個「有題庫」的 KP 當 fallback(graceful:某 KP 自己沒題庫時,
 * 用同單元/同主題的鄰近題庫,保證生得出卷)。找不到回 null。
 */
function bankFallbackInChapter(subject: Subject, kpId: string): string | null {
  const subj = CURRICULUM.find((s) => s.subject === subject);
  if (!subj) return null;
  const chapter = subj.chapters.find((ch) => ch.kps.some((k) => k.id === kpId));
  if (chapter) {
    for (const k of chapter.kps) {
      if (getBankUrl(k.id)) return k.id;
    }
  }
  // 仍找不到 → 全科任一有題庫的 KP(最後保險)。
  for (const ch of subj.chapters) {
    for (const k of ch.kps) {
      if (getBankUrl(k.id)) return k.id;
    }
  }
  return null;
}

/**
 * 決定一個範圍(科目 + KP)的出卷計畫。**永遠回一個可執行 plan**(覆蓋鐵則)。
 *
 * 路由優先序:
 *   1. 數學 + 可映射到產生器大題 → program(math)。
 *   2. 英語 + 在聽力池 KP → program(listening)。
 *   3. 該 KP 有題庫(`getBankUrl`)→ bank(該 KP)。
 *   4. graceful fallback:
 *      - 數學任何其他 KP → program(math) 預設四大題(數學產生器數值鎖三下,通用)。
 *      - 英語任何其他 KP → program(listening)(聽力產生器池通用三下範圍)。
 *      - 國/自/社 → 同章節 / 全科鄰近題庫 bank。
 *      - 真的什麼都沒有 → bank(原 KP)當佔位(UI 端會顯示抽不到題的友善訊息)。
 */
export function planExamForRange(subject: Subject, kpId: string): ExamPlan {
  const label = labelOf(subject, kpId);

  // 0. 數學專屬程式產生器(統計表…)→ 比題庫優先(boss 要程式可驗證統計表卷)。
  if (subject === 'math' && kpId in MATH_SPECIAL_PROGRAM) {
    return programSpecial(subject, kpId, label, MATH_SPECIAL_PROGRAM[kpId]);
  }

  // 1. 數學 → 程式產生器(明確映射)。
  if (subject === 'math' && kpId in MATH_KP_TO_SECTIONS) {
    return programMath(subject, kpId, label, MATH_KP_TO_SECTIONS[kpId]);
  }

  // 2. 英語聽力池 → 聽力產生器。
  if (subject === 'english' && ENGLISH_LISTENING_KPS.has(kpId)) {
    return programListening(subject, kpId, label);
  }

  // 3. 該 KP 有題庫 → 題庫組卷。
  if (getBankUrl(kpId)) {
    return bankPlan(subject, kpId, label, kpId);
  }

  // 4. graceful fallback。
  if (subject === 'math') {
    // 數學沒映射也沒題庫 → 用產生器預設四大題(三下數值通用)。
    return programMath(subject, kpId, label, []);
  }
  if (subject === 'english') {
    // 英語沒題庫 → 聽力產生器(池為三下通用範圍)。
    return programListening(subject, kpId, label);
  }
  // 國 / 自 / 社 → 同章節 / 全科鄰近題庫。
  const fb = bankFallbackInChapter(subject, kpId);
  if (fb) return bankPlan(subject, kpId, label, fb);

  // 真的什麼都沒有(理論上不會到):回 bank(原 KP),UI 端顯示友善訊息。
  return bankPlan(subject, kpId, label, kpId);
}

/**
 * 決定一個**單元**(可能含多個 KP)的出卷計畫,把整個單元**合成一張卷**。
 * boss「點單元 → 直接出那單元一整張卷,不再多一層選 KP;一個單元對多個 KP/section
 * 就合成一張」。
 *
 * 合成原則(沿用 `planExamForRange` 的優先序,但跨整個單元聚合):
 *   1. 單元任一 KP 命中數學專屬程式產生器(統計表 / 時間)→ program(stats-table/time)。
 *      —— 統計表 / 時間單元各只有一種專屬產生器,直接用。
 *   2. 數學單元:把所有「映射到 generateMathExam 大題」的 KP 之 section **聯集**
 *      (去重、保序)→ program(math),一張卷涵蓋整單元概念。無任何映射 KP →
 *      預設七大題(空陣列)。
 *   3. 英語單元任一 KP 在聽力池 → program(listening)。
 *   4. 其他(國 / 自 / 社,或英語無聽力池)→ bank,撈代表 KP(無題庫則同章 fallback)。
 *
 * `kpId` / `label` 用單元的代表 KP 與單元標題(`unitTitle`)。**永遠回可執行 plan**。
 */
export function planExamForUnit(subject: Subject, unit: ExamUnit): ExamPlan {
  const label = unit.title;

  // 1. 數學專屬程式產生器(統計表 / 時間)— 單元任一 KP 命中即用(✅ 可驗證,優先)。
  if (subject === 'math') {
    const special = unit.kpIds.find((id) => id in MATH_SPECIAL_PROGRAM);
    if (special) {
      const plan = programSpecial(subject, special, label, MATH_SPECIAL_PROGRAM[special]);
      plan.kpId = unit.kpId;
      return plan;
    }
    // 2. 數學:聯集單元內所有映射 section(去重保序)→ program(math)。
    //    除法 / 乘法與除法等「有對應產生器大題」的單元走這條(✅ 程式產生)。
    const merged: MathSectionKind[] = [];
    for (const id of unit.kpIds) {
      const secs = MATH_KP_TO_SECTIONS[id];
      if (!secs) continue;
      for (const s of secs) if (!merged.includes(s)) merged.push(s);
    }
    if (merged.length > 0) {
      return programMath(subject, unit.kpId, label, merged);
    }
    // 3. 數學無映射 / 無專屬產生器的單元(分數加減 / 公斤公克 / 圓…)→ 優先該單元題庫
    //    (📚,boss 指定這幾個走題庫卷,較貼單元)。撈代表 KP 或同單元任一有題庫的 KP。
    const bankId =
      (getBankUrl(unit.kpId) ? unit.kpId : null) ?? unit.kpIds.find((id) => getBankUrl(id)) ?? null;
    if (bankId) {
      return bankPlan(subject, unit.kpId, label, bankId);
    }
    // 4. 連題庫都沒有(尋找規律 / 小數,目前無 bank)→ 數學產生器預設七大題保底
    //    (覆蓋鐵則:按了一定出得了一張卷,不會死按鈕)。
    return programMath(subject, unit.kpId, label, []);
  }

  // 3. 英語:單元任一 KP 在聽力池 → 聽力卷。
  if (subject === 'english') {
    if (unit.kpIds.some((id) => ENGLISH_LISTENING_KPS.has(id))) {
      return programListening(subject, unit.kpId, label);
    }
    // 英語其餘 → 聽力產生器(池為三下通用),仍可驗證。
    return programListening(subject, unit.kpId, label);
  }

  // 4. 國 / 自 / 社 → 題庫:用代表 KP(無題庫則同章 / 全科 fallback)。
  if (getBankUrl(unit.kpId)) {
    return bankPlan(subject, unit.kpId, label, unit.kpId);
  }
  for (const id of unit.kpIds) {
    if (getBankUrl(id)) return bankPlan(subject, unit.kpId, label, id);
  }
  const fb = bankFallbackInChapter(subject, unit.kpId);
  if (fb) return bankPlan(subject, unit.kpId, label, fb);
  return bankPlan(subject, unit.kpId, label, unit.kpId);
}

// ───────────────────────────── plan builders ─────────────────────────────

function programMath(
  subject: Subject,
  kpId: string,
  label: string,
  mathKps: MathSectionKind[],
): ExamPlan {
  return {
    subject,
    kpId,
    label,
    method: 'program',
    methodLabel: METHOD_LABEL.program,
    programKind: 'math',
    mathKps,
  };
}

function programSpecial(
  subject: Subject,
  kpId: string,
  label: string,
  kind: MathSpecialProgram,
): ExamPlan {
  return {
    subject,
    kpId,
    label,
    method: 'program',
    methodLabel: METHOD_LABEL.program,
    programKind: kind,
  };
}

function programListening(subject: Subject, kpId: string, label: string): ExamPlan {
  return {
    subject,
    kpId,
    label,
    method: 'program',
    methodLabel: METHOD_LABEL.program,
    programKind: 'listening',
  };
}

function bankPlan(subject: Subject, kpId: string, label: string, bankKpId: string): ExamPlan {
  return {
    subject,
    kpId,
    label,
    method: 'bank',
    methodLabel: METHOD_LABEL.bank,
    bankKpId,
  };
}

/** 解析 KP id → 課程顯示名(找不到回 kpId)。 */
function labelOf(subject: Subject, kpId: string): string {
  const subj = CURRICULUM.find((s) => s.subject === subject);
  if (!subj) return kpId;
  for (const ch of subj.chapters) {
    for (const k of ch.kps) {
      if (k.id === kpId) return k.label;
    }
  }
  return kpId;
}

/** 匯出供測試 / UI 用的數學映射表(唯讀)。 */
export const MATH_SECTION_MAP: Readonly<Record<string, MathSectionKind[]>> = MATH_KP_TO_SECTIONS;
/** 匯出數學專屬程式產生器映射(統計表 / 時間…,唯讀)。 */
export const MATH_SPECIAL_PROGRAM_MAP: Readonly<Record<string, MathSpecialProgram>> = MATH_SPECIAL_PROGRAM;
/** 匯出英語聽力 KP 集合(唯讀)。 */
export const LISTENING_KP_SET: ReadonlySet<string> = ENGLISH_LISTENING_KPS;
